import { Injectable, signal } from '@angular/core';

export type AppTheme = 'dark' | 'light';

/** Chave no localStorage — mesma usada pelo script inline em index.html. */
export const THEME_STORAGE_KEY = 'givo.theme';

/** Tema usado quando o dispositivo não tem preferência salva. */
export const DEFAULT_THEME: AppTheme = 'dark';

function isAppTheme(value: unknown): value is AppTheme {
  return value === 'dark' || value === 'light';
}

/**
 * Tema visual do painel (dark é o padrão).
 *
 * A preferência é do dispositivo: fica só no localStorage, não é sincronizada
 * por API. O tema é aplicado como `data-theme` no <html>; os tokens de cor
 * vivem em `src/styles/_theme.scss`.
 *
 * O tema inicial já é aplicado pelo script inline de `index.html` antes do
 * primeiro paint (evita flash claro/escuro no boot). Este serviço apenas lê o
 * mesmo valor e passa a ser a fonte da verdade em runtime.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _theme = signal<AppTheme>(this.readStoredTheme() ?? DEFAULT_THEME);

  /** Tema ativo. */
  readonly theme = this._theme.asReadonly();

  constructor() {
    // Reaplica no boot: cobre o caso de o script inline não ter rodado
    // (ex.: SSR/prerender) e mantém DOM e signal em sincronia.
    this.applyToDocument(this._theme());
  }

  isDark(): boolean {
    return this._theme() === 'dark';
  }

  set(theme: AppTheme): void {
    if (this._theme() === theme) return;
    this._theme.set(theme);
    this.applyToDocument(theme);
    this.persist(theme);
  }

  toggle(): void {
    this.set(this._theme() === 'dark' ? 'light' : 'dark');
  }

  private applyToDocument(theme: AppTheme): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // Dark é o padrão do CSS — sem atributo o :root já responde como dark.
    if (theme === 'dark') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
  }

  private readStoredTheme(): AppTheme | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return isAppTheme(stored) ? stored : null;
    } catch {
      // Modo privativo / storage bloqueado — segue com o padrão.
      return null;
    }
  }

  private persist(theme: AppTheme): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Preferência não persiste, mas o tema da sessão continua aplicado.
    }
  }
}
